// /sms-consent — a public, no-login record of how MySocialCal asks for consent to
// text a member, with a screenshot of each screen. Public like /privacy and
// /terms (main.ts's PUBLIC_PATHS, NOT bare in chrome.ts).
//
// It exists because the A2P 10DLC campaign's opt-in proof needs a link a carrier
// reviewer can open WITHOUT an account, and two of the screens (number entry, the
// notification grid) are behind login. The screenshots are static files in
// web/public/consent-shots/, captured from a dev build with test data only: no real
// member's number or name appears in any of them. Re-capture them whenever the
// screens they show change, or this page starts describing a flow that is gone.
//
// Wording must stay in sync with the registered consent text in
// docs/a2p-campaign.md; do not reword the consent paragraphs independently.

import { type View } from '../router.js';
import { el } from '../dom.js';
import { authBrand } from './authBrand.js';

function shot(file: string, alt: string): HTMLElement {
  return el('img', {
    class: 'consent-shot',
    src: `/consent-shots/${file}`,
    alt,
    loading: 'lazy',
  });
}

export const smsConsentView: View = (outlet) => {
  outlet.append(
    el('main', { class: 'page' }, [
      authBrand(),
      el('h1', {}, 'How MySocialCal asks for text message consent'),
      el('p', {}, [
        'MySocialCal is operated by IT Summit Services LLC. We text only registered ',
        'members, only a number its owner entered themselves and proved with a ',
        'one-time code, and only the kinds of message that member chose. The ',
        'screens below are what a member sees. The sample data on them is test data.',
      ]),

      el('h2', {}, '1. Sign up, choosing "Text message"'),
      el('p', {}, [
        'The sign-up form at ',
        el('a', { href: '/register' }, 'mysocialcal.com/register'),
        ' asks how we should reach the person and shows the consent notice ',
        'directly under the number field, before anything is sent.',
      ]),
      shot('1-register.jpg', 'The MySocialCal sign-up form with Text message selected and the consent notice under the mobile number field'),

      el('h2', {}, '2. Or add a number later, from the account page'),
      el('p', {}, [
        'A member who signed up by email can add a number from their account page. ',
        'The same consent notice sits under the field.',
      ]),
      shot('2-account-number.jpg', 'The account page phone number card with the consent notice and a Send me a code button'),

      el('h2', {}, '3. Enter the code we text'),
      el('p', {}, [
        'We text a one-time code to the number. It becomes active for messaging only ',
        'when the account holder enters that code on the site. Until then it is held as ',
        'pending and nothing but that one code is sent to it.',
      ]),
      shot('3-account-code.jpg', 'The verification code entry form, stating the number is not on the account until the code checks out'),

      el('h2', {}, '4. Choose what to receive, unchecked by default'),
      el('p', {}, [
        'After the number is verified, the first screen after sign-up offers a box, ',
        'unchecked, listing the kinds of text message. Nothing is turned on unless the ',
        'member ticks it. Email is the default channel, so text is always an extra, ',
        'affirmative choice.',
      ]),
      shot('4-welcome.jpg', 'The first-run screen with an unchecked box to receive text messages and the list of message kinds'),

      el('h2', {}, '5. Change it any time, per kind and per group'),
      el('p', {}, [
        'Notification settings has one switch for each kind of message and each channel ',
        '(email, text, push), as defaults and again per group.',
      ]),
      shot('5-settings.jpg', 'The notification settings grid with Email, Text and Push columns for each kind of message'),

      el('h2', {}, 'The kinds of text message'),
      el('ul', {}, [
        el('li', {}, 'A verification code for a number the member just entered'),
        el('li', {}, 'A notice that an organizer added them to a group'),
        el('li', {}, 'A new event in one of their groups'),
        el('li', {}, 'The day-of "still coming?" prompt'),
        el('li', {}, 'An attendance headcount, to the event’s organizer'),
        el('li', {}, 'An event that is on, is full, or is no longer on'),
        el('li', {}, 'A new post in one of their groups'),
        el('li', {}, 'New messages in an event’s chat'),
        el('li', {}, 'A summary of RSVP changes, to the event’s organizer'),
        el('li', {}, 'A request to join a group, to its owners and admins'),
      ]),
      el('p', {}, [
        'Texts about posts and chat only say that there is something new and link to ',
        'it. They never contain what a member wrote. There is no marketing.',
      ]),

      el('h2', {}, 'Stopping'),
      el('p', {}, [
        'Reply ',
        el('strong', {}, 'STOP'),
        ' to any message to opt out, or ',
        el('strong', {}, 'HELP'),
        ' for help. Members can also switch any kind off in notification settings or ',
        'delete their number from their account page. Message and data rates may apply.',
      ]),
      el('p', {}, [
        el('a', { href: '/privacy' }, 'Privacy Policy'),
        ' · ',
        el('a', { href: '/terms' }, 'Terms and Conditions'),
      ]),
    ]),
  );
};
